import { useRef, useState } from 'react'
import { modal } from '@codeleap/modals'
import { WebInputFile } from '@codeleap/types'
import { ActionIcon, FileInputRef, Touchable } from '@codeleap/web'
import { View, Button, FileInput, Text } from '../../..'
import { AppIcon } from '@/config'
import { createStyles } from '@codeleap/styles'
import { compressFileImages, convertHeicImages } from '@/utils/imageConverter'

type AttachmentTypes = keyof typeof fileExtPerType

type RowProps = { icon: AppIcon; text: string; onPress: () => void }

const fileExtPerType = {
  image: '.png, .jpg, .jpeg, .gif, .webp, .bmp, .tiff, .svg, .heic, .heif',
  file: '',
}

const Row = ({ icon, text, onPress }: RowProps) => (
  <Touchable
    debugName={'AttachmentModal:Row'}
    onPress={onPress}
    style={styles.rowWrapper}
  >
    <ActionIcon icon={icon} onPress={onPress} />
    <Text text={text} style={['p3', 'medium', 'flex']} />
  </Touchable>
)

type ModalProps = {
  onGIFPress: () => void
  onPollPress?: () => void
  showPollOption?: boolean
}

export const AttachmentModal = modal<ModalProps, WebInputFile[]>({ id: 'attachment-modal' }).content((props) => {
  const { request, toggle, onGIFPress, onPollPress, showPollOption = true } = props

  AttachmentModal.useProps({
    dismissOnBackdrop: true,
    showClose: false,
    style: ['centered', { box: ['box:noPadding', 'fullWidth', styles.box] }],
  })

  const fileInputRef = useRef<FileInputRef>(null)
  const [type, setType] = useState<AttachmentTypes>('file')

  const openFilePicker = (t: AttachmentTypes) => {
    return () => {
      setType(t)
      setTimeout(() => fileInputRef.current.openFilePicker())
    }
  }

  const onFileSelect = async (files: WebInputFile[]) => {
    const processedFiles = await convertHeicImages(files.map(f => f.file))
    const compressedFiles = await compressFileImages(processedFiles)

    const webInputFiles: WebInputFile[] = compressedFiles.map(file => ({
      file,
      preview: URL.createObjectURL(file),
    }))

    request.resolve(webInputFiles)
  }

  return (
    <View style={['fullWidth', 'column', 'gap:3', 'paddingVertical:3']}>
      <View style={['flex', 'column']}>
        <Row text={'Photos'} icon={'image-01'} onPress={openFilePicker('image')} />
        <Row text={'Files'} icon={'attachment-01'} onPress={openFilePicker('file')} />
        <Row
          text={'GIF'}
          icon={'sticker-circle'}
          onPress={() => {
            onGIFPress()
            toggle()
          }}
        />
        {showPollOption && (
          <Row
            text={'Poll'}
            icon={'menu-05'}
            onPress={() => {
              onPollPress?.()
              toggle()
            }}
          />
        )}

      </View>

      <View style={['fullWidth', 'paddingHorizontal:3']}>
        <Button
          text='Cancel'
          debugName='Cancel Upload - ChatAttachmentModal'
          onPress={toggle}
          style={['large', 'fullWidth', 'borderRadius:regular', 'neutralSecondary']}
        />
      </View>

      <FileInput
        multiple
        accept={fileExtPerType[type]}
        ref={fileInputRef}
        onFileSelect={(f) => onFileSelect(f)}
      />

    </View>
  )

})

const styles = createStyles(theme => ({
  box: {
    maxWidth: 343,
  },
  rowWrapper: {
    ...theme.presets.row,
    ...theme.presets.alignCenter,
    ...theme.spacing.gap(1.5),
    ...theme.presets.fullWidth,
    ...theme.spacing.paddingHorizontal(3),
    ...theme.spacing.paddingVertical(1.5),
    ...theme.border({
      color: 'neutralBlackTransparent100',
      width: 1,
      directions: ['bottom'],
    }),
  },
}))
